/**
 * SSO handoff token — taken out of the URL before anything can observe it.
 *
 * The backend hands a freshly minted access token to the SPA through the
 * address bar: as `?jwt=<token>` on the login route, and (SMS) as a trailing
 * path segment on `/oauth-success/<token>`. That URL is a credential, and it
 * is observed by more than the page:
 *
 *   - **Sentry Session Replay records it, and no scrubber runs.** Replay
 *     starts *synchronously inside `Sentry.init`* — `afterAllSetup` →
 *     `_initialize` → `initializeSampling` → `_initializeRecording` →
 *     `setInitialState`, which reads `location.search` into
 *     `_context.urls` (shipped as the replay envelope's `urls` field) while
 *     rrweb's opening Meta event captures `location.href`. Neither passes
 *     through `beforeSend`, so `maskAllText` / `blockAllMedia` and the
 *     `piiRedactor` are all irrelevant to it. A strip that runs in a
 *     `useEffect` is far too late: the component has not mounted yet.
 *   - Browser history and outbound `Referer` headers keep it after that.
 *
 * So the token is consumed at boot — **before `Sentry.init`, before React
 * renders** — and held in module scope. `replaceState` runs before Sentry's
 * history instrumentation is installed, so it does not even register as a
 * navigation.
 *
 * Wiring, in each app's `main.tsx`:
 *
 *     consumeHandoffJwt({ pathPrefixes: ['/oauth-success'] });
 *     initializeSentry();
 *
 * Order is load-bearing and pinned by test in both apps.
 *
 * Routes then read {@link getHandoffJwt} or {@link takeHandoffJwt} instead of
 * the URL. This is safe because every token arrival is a full page load — the
 * `/oauth-success` handoff redirects with `window.location.href`, and the SSO
 * popup returns by navigation. Nothing routes to a token-bearing URL
 * client-side, and nothing may start to: a token that appears after boot is a
 * token Replay has already recorded.
 *
 * The captured token outlives the route that used it, for the rest of the
 * page load. A route that can mount again within the same page load (a
 * client-side sign-out back to `/login`, say) must use
 * {@link takeHandoffJwt}, or it re-submits the credential.
 *
 * The tokens are credentials for student-record systems
 * (Restricted-Student-Record-PII), so this is a FERPA / CCPA-CPRA control,
 * not hygiene. See ADR-098 (credential lifecycle) and ADR-099 §3.
 */

/** Query parameter carrying the SSO access token. */
export const HANDOFF_JWT_PARAM = 'jwt';

/**
 * Token charset for the path form, matching the pattern the SSO popup
 * pollers already accept (base64url, plus the `+`/`=` of standard base64).
 */
const PATH_TOKEN = /^[A-Za-z0-9+=\-._]+$/;

export interface ConsumeHandoffJwtOptions {
  /**
   * Route prefixes that carry the token as a trailing path segment, e.g.
   * `'/oauth-success'` for `/oauth-success/<token>`. Deleting a query
   * parameter cannot reach that shape, so it is matched and rewritten
   * separately.
   */
  pathPrefixes?: string[];
}

let captured: string | null = null;
let hasConsumed = false;

/**
 * Read the handoff token out of the URL and erase it from the address bar.
 *
 * Call once, at the top of `main.tsx`, before any telemetry is initialized.
 * Returns the token so a caller can act on it directly; routes should
 * prefer {@link getHandoffJwt}.
 */
export function consumeHandoffJwt(
  options: ConsumeHandoffJwtOptions = {},
): string | null {
  hasConsumed = true;

  const { pathPrefixes = [] } = options;
  const params = new URLSearchParams(window.location.search);

  let token = params.get(HANDOFF_JWT_PARAM);
  let pathname = window.location.pathname;
  let dirty = false;

  if (token !== null) {
    params.delete(HANDOFF_JWT_PARAM);
    dirty = true;
  }

  for (const prefix of pathPrefixes) {
    if (!pathname.startsWith(`${prefix}/`)) continue;

    // `/oauth-success/<token>` and `/oauth-success/<token>/` — one segment,
    // nothing deeper, so an unrelated nested route is never rewritten.
    const rest = pathname.slice(prefix.length + 1).replace(/\/$/, '');
    if (!rest || !PATH_TOKEN.test(rest)) continue;

    // A query token wins if both shapes somehow arrive: it is the form the
    // redirect handoff mints. The path is rewritten either way.
    if (token === null) token = rest;
    pathname = prefix;
    dirty = true;
    break;
  }

  captured = token;

  if (dirty) {
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      `${pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`,
    );
  }

  return captured;
}

/**
 * The token this page load arrived with, or `null` if none arrived or
 * {@link takeHandoffJwt} has taken it.
 *
 * Pure accessor — it never reads the URL. If it returns `null` where a token
 * was expected, `consumeHandoffJwt()` did not run before the app rendered.
 */
export function getHandoffJwt(): string | null {
  return captured;
}

/**
 * Single-use read of the token this page load arrived with. Returns it once,
 * then `null` to every later caller, {@link getHandoffJwt} included.
 */
export function takeHandoffJwt(): string | null {
  const token = captured;
  captured = null;
  return token;
}

/** Whether {@link consumeHandoffJwt} has run. Wiring assertions and tests. */
export function hasConsumedHandoffJwt(): boolean {
  return hasConsumed;
}

/** Test seam — module state is per page load in production. */
export function resetHandoffJwtForTests(): void {
  captured = null;
  hasConsumed = false;
}
